import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Stack,
} from "@mui/material";
import TableCompo from "../../CommonTable/TableCompo";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getProductOrderDataRequest } from "../../../redux/ProductOrder/productOrderSlice";
import { getBullionRatesRequest } from "../../../redux/Dashboard/dashboardSlice";
import { useAppTheme } from "../../../theme/useAppTheme";
import useCurrency from "../../../hooks/useCurrency";

const ProductOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { bullionRates, appConfig } = useSelector(
    (state) => state.dashboard || {},
  );
  const decimal = appConfig?.decimalPrecision ?? 4;
  const productState = useSelector((s) => s.productOrder);
  const { isLoading, data } = productState || {};

  const themeColors = useAppTheme();
  const { symbol } = useCurrency();

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    dispatch(
      getBullionRatesRequest({ id: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
    dispatch(getProductOrderDataRequest());
  }, [dispatch]);

  const productList = data?.data || [];

  const [filter, setFilter] = useState("All");

  const CDN_URL = process.env.REACT_APP_CDN_URL;
  const packageName = process.env.REACT_APP_PACKAGE_NAME;

  const toImageUrl = (img) => {
    if (!img || typeof img !== "string") return "";
    if (img.startsWith("http")) return img;
    return `${CDN_URL}/${packageName}/${img.replace(/^\//, "")}`;
  };

  const transformedData = productList.map((item, idx) => {
    const rawUrls = Array.isArray(item.productdetails?.url)
      ? item.productdetails.url
      : [];
    const imageUrls = rawUrls.map(toImageUrl).filter(Boolean);

    return {
      _id: item._id,
      status: item.status || "Pending",
      bookingstatus: item.bookingstatus || "PENDING",
      transactions: item.transactions || {},
      amount: item.amount,
      order_id: item.order_id,

      product: {
        ...item.productdetails,
        name: item.productdetails?.name || "",
        url: imageUrls,
        images: imageUrls,
        metalWeight: item.productdetails?.metal_weight || 0,
        purity: item.productdetails?.purity || 0,
        metalname: item.productdetails?.metalname || "",
      },

      finalPrice: item.amount || 0,

      productPriceDetails: {
        finalPrice: item.amount || 0,
        makingCharges: 0,
        taxAmount: item.transactions?.total_tax || 0,
      },
    };
  });

  const tableData = transformedData.filter((item) => {
    const match =
      filter === "All" ||
      (item.status || "").toUpperCase() === filter.toUpperCase();

    return match;
  });
  const paginatedData = tableData.slice(
    page * limit,
    page * limit + limit
  );

  const getColumns = () => [
    {
      field: "productName",
      headerName: "Product Name",
      flex: 1,
      render: (val, row) => row.product.name,
    },
    {
      field: "metalInfo",
      headerName: "Metal & Purity",
      flex: 1,
      render: (val, row) => `${row.product.metalname} - ${row.product.purity}`,
    },
    {
      field: "weight",
      headerName: "Weight (gms)",
      flex: 1,
      render: (val, row) => `${row.product.metalWeight}`,
    },

    {
      field: "finalPrice",
      headerName: `Price (${symbol})`,
      flex: 1,
      render: (val, row) => row.finalPrice?.toFixed(decimal),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      render: (val, row) => row.status,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      render: (val, row) => (
        <Button
          variant="contained"
          size="small"
          sx={{
            textTransform: "none",
            borderRadius: "20px",
            background: themeColors.mediumContainer,
            color: themeColors.text,
          }}
          onClick={() =>
            navigate("/productOrderDetails", {
              state: {
                pendingOrder: row,
                priceObj: row.productPriceDetails,
              },
            })
          }
        >
          View Details
        </Button>
      ),
    },
  ];
  const handleTableChange = (type, payload) => {
    if (type === "changePage") {
      setPage(payload.page);
    }

    if (type === "changeRowsPerPage") {
      setLimit(payload.rowsPerPage);
      setPage(0);
    }
  };

  return (
    <Box
      sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 4 }}
    >
      <Card
        sx={{
          width: "98%",
          maxWidth: "1300px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          borderRadius: 3,
          background: themeColors.scaffoldGradientColors,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            sx={{ mb: 2, fontWeight: 700, color: themeColors.primary }}
          >
            Product Orders
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            {[
              "All",
              "PENDING",
              "RETURNED",
              "SUCCESS",
              "DISPATCHED",
              "READYTODISPATCH",
            ].map((status) => (
              <Button
                key={status}
                variant={filter === status ? "contained" : "outlined"}
                size="small"
                onClick={() => setFilter(status)}
                sx={{
                  textTransform: "none",
                  borderRadius: "20px",
                  px: 2,
                  fontWeight: 600,
                  color: themeColors.text,
                  background: themeColors.primary,
                  color: themeColors.text,
                  "&:hover": {
                    background: themeColors.svgColor,
                  },
                  border: `1px solid ${themeColors.scaffold}`,
                }}
              >
                {status}
              </Button>
            ))}
          </Stack>

          {isLoading ? (
            <Box sx={{ textAlign: "center", mt: 10 }}>
              <CircularProgress color="primary" />
              <Typography variant="body2" mt={2}>
                Loading product orders...
              </Typography>
            </Box>
          ) : tableData.length === 0 ? (
            <Typography textAlign="center" mt={5}>
              No product orders found
            </Typography>
          ) : (
            <Box sx={{ height: "70vh", width: "100%", minWidth: 0, overflow: "auto" }}>
              <TableCompo
                title="Product Orders"
                columns={getColumns()}
                data={paginatedData}
                page={page}
                limit={limit}
                totalCount={tableData.length}
                onTableChange={handleTableChange}
                loading={isLoading}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductOrder;
